import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Tooltip,
  Fab,
  InputAdornment,
  Card,
  CardContent,
  DialogContentText,
  CircularProgress,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Computer as ComputerIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  School as SchoolIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { 
  getSchoolProjects, 
  getSchoolProjectById,
  createSchoolProject,
  updateSchoolProject,
  deleteSchoolProject,
  getSchools
} from '../../services/api';
import dayjs from 'dayjs';

const ProjectsList = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [formMode, setFormMode] = useState('add');

  useEffect(() => {
    fetchProjects();
    fetchSchools();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await getSchoolProjects();
      if (response.success) {
        setProjects(response.data || []);
      } else {
        showError(response.message || 'Failed to load projects');
      }
    } catch (err) {
      showError(err.message || 'Failed to load projects');
      console.error('Projects error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchools = async () => {
    try {
      const response = await getSchools();
      if (response.success) {
        setSchools(response.data || []);
      }
    } catch (err) {
      console.error('Schools error:', err);
    }
  };

  const showSuccess = (message) => {
    setSnackbarMessage(message);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const showError = (message) => {
    setSnackbarMessage(message);
    setSnackbarSeverity('error');
    setSnackbarOpen(true);
  };

  const handleDeleteClick = (project) => {
    setSelectedProject(project);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteSchoolProject(selectedProject.id);
      if (response.success) {
        showSuccess('Project deleted successfully!');
        setOpenDeleteDialog(false);
        fetchProjects();
      } else {
        showError(response.message || 'Failed to delete project');
      }
    } catch (err) {
      showError(err.message || 'Failed to delete project');
    }
  };

  const handleView = async (project) => {
    try {
      const response = await getSchoolProjectById(project.id);
      if (response.success) {
        setSelectedProject(response.data);
        setOpenViewDialog(true);
      } else {
        showError(response.message || 'Failed to load project details');
      }
    } catch (err) {
      showError(err.message || 'Failed to load project details');
    }
  };

  const handleEdit = (project) => {
    setSelectedProject(project);
    setFormMode('edit');
    setOpenFormDialog(true);
  };

  const handleAddProject = () => {
    setSelectedProject(null);
    setFormMode('add');
    setOpenFormDialog(true);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'info',
      in_progress: 'warning',
      completed: 'success',
      delivered: 'secondary',
      installed: 'primary',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'Pending',
      in_progress: 'In Progress',
      completed: 'Completed',
      delivered: 'Delivered',
      installed: 'Installed',
      cancelled: 'Cancelled'
    };
    return texts[status] || status;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'Not set';
    return dayjs(date).format('DD MMM YYYY');
  };

  const calculateProgress = (project) => {
    if (project.lab_size > 0) {
      return Math.round((project.installed_computers / project.lab_size) * 100);
    }
    return 0;
  };

  const filteredProjects = projects.filter(project => {
    // Apply search filter
    if (searchQuery && 
        !project.project_name?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !project.School?.school_name?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Apply status filter
    if (statusFilter !== 'all' && project.status !== statusFilter) {
      return false;
    }
    
    return true;
  });

  const ProjectForm = () => {
    const [formData, setFormData] = useState({
      project_name: '',
      school_id: '',
      lab_size: '0',
      total_computers: '0',
      estimated_cost: '0',
      status: 'pending',
      start_date: dayjs().format('YYYY-MM-DD'),
      estimated_completion_date: '',
      notes: ''
    });
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);

    useEffect(() => {
      if (formMode === 'edit' && selectedProject) {
        setFormData({
          project_name: selectedProject.project_name || '',
          school_id: selectedProject.school_id || '',
          lab_size: selectedProject.lab_size || '0',
          total_computers: selectedProject.total_computers || '0',
          estimated_cost: selectedProject.estimated_cost || '0',
          status: selectedProject.status || 'pending',
          start_date: selectedProject.start_date ? dayjs(selectedProject.start_date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
          estimated_completion_date: selectedProject.estimated_completion_date ? dayjs(selectedProject.estimated_completion_date).format('YYYY-MM-DD') : '',
          notes: selectedProject.notes || ''
        });
      }
    }, [formMode, selectedProject]);

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
      
      // Auto-set total computers when lab size changes
      if (name === 'lab_size') {
        setFormData(prev => ({ 
          ...prev, 
          lab_size: value,
          total_computers: value || '0'
        }));
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setFormError(null);

      if (!formData.project_name || !formData.school_id) {
        setFormError('Project name and school are required');
        return;
      }

      try {
        setSaving(true);
        const dataToSend = {
          ...formData,
          lab_size: parseInt(formData.lab_size) || 0,
          total_computers: parseInt(formData.total_computers) || 0,
          estimated_cost: parseFloat(formData.estimated_cost) || 0
        };

        if (formMode === 'edit' && selectedProject) {
          const response = await updateSchoolProject(selectedProject.id, dataToSend);
          if (response.success) {
            showSuccess('Project updated successfully!');
            setOpenFormDialog(false);
            fetchProjects();
          } else {
            setFormError(response.message || 'Failed to update project');
          }
        } else {
          const response = await createSchoolProject(dataToSend);
          if (response.success) {
            showSuccess('Project created successfully!');
            setOpenFormDialog(false);
            fetchProjects();
          } else {
            setFormError(response.message || 'Failed to create project');
          }
        }
      } catch (err) {
        setFormError(err.message || 'Failed to save project');
      } finally {
        setSaving(false);
      }
    };

    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          {formMode === 'edit' ? 'Edit Project' : 'Create New Project'}
        </Typography>
        
        {formError && (
          <Alert severity="error" sx={{ mb: 3 }}>{formError}</Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project Name *"
                name="project_name"
                value={formData.project_name}
                onChange={handleChange}
                required
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>School *</InputLabel>
                <Select
                  name="school_id"
                  value={formData.school_id}
                  onChange={handleChange}
                  label="School *"
                >
                  <MenuItem value="">Select School</MenuItem>
                  {schools.map((school) => (
                    <MenuItem key={school.id} value={school.id}>
                      {school.school_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Lab Size (Computers)"
                name="lab_size"
                value={formData.lab_size}
                onChange={handleChange}
                type="number"
                InputProps={{ inputProps: { min: 0 } }}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Computers"
                name="total_computers"
                value={formData.total_computers}
                onChange={handleChange}
                type="number"
                InputProps={{ inputProps: { min: 0 } }}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Estimated Cost (LKR)"
                name="estimated_cost"
                value={formData.estimated_cost}
                onChange={handleChange}
                type="number"
                InputProps={{ 
                  inputProps: { min: 0, step: 0.01 },
                  startAdornment: <InputAdornment position="start">Rs.</InputAdornment>
                }}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Status"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="installed">Installed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                type="date"
                InputLabelProps={{ shrink: true }}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Estimated Completion"
                name="estimated_completion_date"
                value={formData.estimated_completion_date}
                onChange={handleChange}
                type="date"
                InputLabelProps={{ shrink: true }}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                multiline
                rows={3}
                margin="normal"
              />
            </Grid>
          </Grid>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => setOpenFormDialog(false)}
              disabled={saving}
              startIcon={<CloseIcon />}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={saving}
              sx={{ backgroundColor: '#3498db' }}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {saving ? 'Saving...' : (formMode === 'edit' ? 'Update Project' : 'Create Project')}
            </Button>
          </Box>
        </form>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: '#2c3e50', fontWeight: 'bold' }}>
            School Projects
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {filteredProjects.length} projects found
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchProjects}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: '#3498db',
              '&:hover': { backgroundColor: '#2980b9' }
            }}
            onClick={handleAddProject}
          >
            New Project
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Projects
                  </Typography>
                  <Typography variant="h4">{projects.length}</Typography>
                </Box>
                <ComputerIcon sx={{ fontSize: 40, color: '#3498db', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    In Progress
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {projects.filter(p => p.status === 'in_progress').length}
                  </Typography>
                </Box>
                <ComputerIcon sx={{ fontSize: 40, color: '#f39c12', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Completed
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {projects.filter(p => p.status === 'completed' || p.status === 'installed').length}
                  </Typography>
                </Box>
                <ComputerIcon sx={{ fontSize: 40, color: '#2ecc71', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Pending
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {projects.filter(p => p.status === 'pending').length}
                  </Typography>
                </Box>
                <ComputerIcon sx={{ fontSize: 40, color: '#3498db', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter Bar */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          placeholder="Search projects..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="delivered">Delivered</MenuItem>
            <MenuItem value="installed">Installed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Projects Table */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Project Name</strong></TableCell>
              <TableCell><strong>School</strong></TableCell>
              <TableCell><strong>Lab Size</strong></TableCell>
              <TableCell><strong>Progress</strong></TableCell>
              <TableCell><strong>Estimated Cost</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Start Date</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No projects found. Try adjusting your search or filters.
                  </Typography>
                  <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />}
                    onClick={handleAddProject}
                    sx={{ mt: 2 }}
                  >
                    Create New Project
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              filteredProjects.map((project) => (
                <TableRow 
                  key={project.id}
                  hover
                  sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                >
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {project.project_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID: {project.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <SchoolIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {project.School?.school_name || 'Unknown School'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {project.lab_size || 0} computers
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 60, bgcolor: '#e0e0e0', borderRadius: 1 }}>
                        <Box 
                          sx={{ 
                            width: `${calculateProgress(project)}%`,
                            height: 8,
                            bgcolor: calculateProgress(project) >= 100 ? '#2ecc71' : '#3498db',
                            borderRadius: 1
                          }} 
                        />
                      </Box>
                      <Typography variant="body2">
                        {calculateProgress(project)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <MoneyIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {formatCurrency(project.estimated_cost)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getStatusText(project.status)} 
                      size="small"
                      color={getStatusColor(project.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {formatDate(project.start_date)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small" 
                          onClick={() => handleView(project)}
                          sx={{ color: '#3498db' }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Project">
                        <IconButton 
                          size="small"
                          sx={{ color: '#f39c12' }}
                          onClick={() => handleEdit(project)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Project">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteClick(project)}
                          sx={{ color: '#e74c3c' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        {selectedProject && (
          <>
            <DialogTitle>
              <Typography variant="h6">
                Project Details: {selectedProject.project_name}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Project ID:</Typography>
                  <Typography variant="body1">{selectedProject.id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Chip 
                    label={getStatusText(selectedProject.status)} 
                    size="small"
                    color={getStatusColor(selectedProject.status)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">School:</Typography>
                  <Typography variant="body1">{selectedProject.School?.school_name || 'Unknown'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Lab Size:</Typography>
                  <Typography variant="body1">{selectedProject.lab_size} computers</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Total Computers:</Typography>
                  <Typography variant="body1">{selectedProject.total_computers}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Installed Computers:</Typography>
                  <Typography variant="body1">{selectedProject.installed_computers}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Estimated Cost:</Typography>
                  <Typography variant="body1">{formatCurrency(selectedProject.estimated_cost)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Actual Cost:</Typography>
                  <Typography variant="body1">{formatCurrency(selectedProject.actual_cost)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Start Date:</Typography>
                  <Typography variant="body1">{formatDate(selectedProject.start_date)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Estimated Completion:</Typography>
                  <Typography variant="body1">{formatDate(selectedProject.estimated_completion_date)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Actual Completion:</Typography>
                  <Typography variant="body1">{formatDate(selectedProject.actual_completion_date)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Installation Date:</Typography>
                  <Typography variant="body1">{formatDate(selectedProject.installation_date)}</Typography>
                </Grid>
                {selectedProject.statistics && (
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Statistics</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Total Computers:</Typography>
                        <Typography variant="body1">{selectedProject.statistics.total_computers || 0}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Assignments:</Typography>
                        <Typography variant="body1">{selectedProject.statistics.assignments_count || 0}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Completion:</Typography>
                        <Typography variant="body1">{selectedProject.statistics.completion_percentage || 0}%</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                {selectedProject.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Notes:</Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {selectedProject.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
              <Button 
                onClick={() => {
                  setOpenViewDialog(false);
                  handleEdit(selectedProject);
                }}
                variant="contained"
                sx={{ backgroundColor: '#f39c12' }}
              >
                Edit Project
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Form Dialog */}
      <Dialog open={openFormDialog} onClose={() => setOpenFormDialog(false)} maxWidth="md" fullWidth>
        <ProjectForm />
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{selectedProject?.project_name}"? 
            <Typography color="error" sx={{ mt: 1 }}>
              Warning: This action cannot be undone. If this project has computers or assignments, you cannot delete it.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={confirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />

      {/* Quick Add FAB */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          backgroundColor: '#3498db',
        }}
        onClick={handleAddProject}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default ProjectsList;